import React, { forwardRef, useId } from 'react';
import styled, { css } from 'styled-components';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helperText?: string;
  variant?: 'default' | 'filled';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const InputContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const ICON_SLOT_WIDTH = '36px';

const InputWrapper = styled.div<{ hasError?: boolean; size?: string }>`
  position: relative;
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.input};
  border: 1px solid ${({ theme, hasError }) => 
    hasError ? theme.colors.danger : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  transition: all ${({ theme }) => theme.transitions.fast};
  overflow: hidden;

  &:focus-within {
    border-color: ${({ theme, hasError }) => 
      hasError ? theme.colors.danger : theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme, hasError }) => 
      hasError ? 'rgba(255, 68, 68, 0.2)' : theme.colors.primarySoft};
  }

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          min-height: 38px;
        `;
      case 'lg':
        return css`
          min-height: 48px;
        `;
      default:
        return css`
          min-height: 48px;
        `;
    }
  }}
`;

const StyledInput = styled.input<{ $size?: string }>`
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 1.25;
  padding: ${({ theme, $size }) => {
    const vertical = $size === 'sm' ? theme.spacing.xs : theme.spacing.sm;
    return `${vertical} ${theme.spacing.sm}`;
  }};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
  }

  &[type='password'] {
    letter-spacing: 0.05em;
  }

  &::-ms-reveal,
  &::-ms-clear {
    display: none;
  }
`;

const IconSlot = styled.div<{ $position: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 ${ICON_SLOT_WIDTH};
  width: ${ICON_SLOT_WIDTH};
  color: ${({ theme }) => theme.colors.textMuted};
  pointer-events: ${({ $position }) => ($position === 'right' ? 'auto' : 'none')};

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  outline: none;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }
`;

const ErrorText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.danger};
`;

const HelperText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      variant = 'default',
      size = 'md',
      fullWidth = false,
      leftIcon,
      rightIcon,
      className,
      // Sensible default cap so no text field can be typed into indefinitely;
      // override per-field (e.g. maxLength={40} for a username) where needed.
      maxLength = 100,
      ...props
    },
    ref
  ) => {
    const generatedId=useId();
    const inputId=props.id||generatedId;
    return (
      <InputContainer fullWidth={fullWidth} className={className}>
        {label && <Label htmlFor={inputId}>{label}</Label>}
        <InputWrapper hasError={!!error} size={size}>
          {leftIcon && <IconSlot $position="left">{leftIcon}</IconSlot>}
          <StyledInput
            ref={ref}
            id={inputId}
            aria-invalid={!!error}
            $size={size}
            maxLength={maxLength}
            {...props}
          />
          {rightIcon && <IconSlot $position="right">{rightIcon}</IconSlot>}
        </InputWrapper>
        {error && <ErrorText>{error}</ErrorText>}
        {helperText && !error && <HelperText>{helperText}</HelperText>}
      </InputContainer>
    );
  }
);

Input.displayName = 'Input';

export { IconButton };
export default Input;

